import { Button, Card, CardContent } from '@h2hspark/ui';
import { MapPin } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';

import { useDiscoverEvents } from '@/domains/event-discovery/api';
import type { DiscoverEventCard } from '@/domains/event-discovery/types';

import { limitedItems } from './constants';
import { Band, SectionHeading } from './section-band-panel';
import { text, type ContentData } from './types';

/**
 * The prototype's "Events worth showing up for" band (`CMS-010`, the `eventsPreview` section).
 *
 * ## The one section whose content is not content
 *
 * Every other section on a page is either authored on the section (a hero's headline) or read from
 * a published collection (testimonials). This one reads **live events from the events domain at
 * request time**, and `content-types.ts` says so in its header: a renderer that assumes every
 * section holds its own data is wrong about exactly this one, deliberately.
 *
 * It has no fields beyond a heading and a limit, because there is nothing else for an editor to
 * decide. Which events appear is a fact about the events, not an editorial choice — and a section
 * that let somebody pick them by hand would be a second, stale copy of the events list.
 *
 * ## Why it renders nothing when there are no events
 *
 * Same rule as the collection sections: a heading over an empty grid reads as a page that failed
 * to load. A marketing site with no published events yet should show the rest of its landing page,
 * not a band announcing events it does not have.
 *
 * ## Why it reuses the discovery query
 *
 * `useDiscoverEvents` is already public and unauthenticated-capable — the same call serves a
 * signed-in member and a signed-out visitor — and it is the events domain's own HTTP surface. A
 * second endpoint for "the same list, but three of them" would be a second answer to "what events
 * are on", and the two would eventually disagree.
 */

export function EventsPreviewSection({
  section,
}: {
  readonly section: ContentData;
}): React.ReactElement | null {
  const events = useDiscoverEvents({ search: '', atmosphere: '' });
  return (
    <EventsPreview
      heading={text(section, 'heading')}
      anchor={text(section, 'anchor')}
      // Three across is what the design lays out, and it is the default rather than the cap: an
      // editor who wants six can say six.
      eyebrow={text(section, 'eyebrow')}
      events={limitedItems(events.data?.items ?? [], section['limit'] ?? 3)}
    />
  );
}

export function EventsPreview({
  heading,
  events,
  anchor,
  eyebrow,
}: {
  readonly heading: string;
  readonly events: ReadonlyArray<DiscoverEventCard>;
  readonly anchor?: string | undefined;
  readonly eyebrow?: string | undefined;
}): React.ReactElement | null {
  if (events.length === 0) return null;

  return (
    <Band anchor={anchor}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          {eyebrow !== undefined && eyebrow !== '' && (
            <p className="h2h-eyebrow mb-2">{eyebrow}</p>
          )}
          <SectionHeading>{heading}</SectionHeading>
        </div>
        {/* The design's neutral outline pill — `border:1.5px solid var(--border-default);
            background:var(--surface-card); color:var(--text-strong); font-brand 700;
            font-size:var(--text-sm); padding:11px 18px`. It was `size="sm"` with a hand-added
            corner, which resolved to shadcn's grey 1px square-ish button at 12px (TW 44160061,
            TW 44165107). The shape and the type are the shared button's own now. */}
        <Button asChild variant="outline">
          <Link to="/app">Browse all events</Link>
        </Button>
      </div>
      <ul className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {events.map((event) => (
          <li key={event.eventId}>
            {/* **The whole card opens the event** — TW 44159483. The design's card carries
                `onClick="{{ enterApp }}"` and `cursor:pointer` on the outer element; ours put the
                link on the name alone, so the picture — the biggest and most obvious thing to aim
                at — did nothing. The name keeps its own underline on hover through `group-hover`,
                so it still reads as the link. One link per card rather than a second one inside
                it: nesting anchors is invalid, and two tab stops to one destination is worse for a
                keyboard reader, not better. */}
            <Link
              to={`/app/events/${event.eventId}`}
              className="group block h-full rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
            <Card className="h-full overflow-hidden">
              {/* The cover, 140px in the design, with the **atmosphere chip** on it. The chip was
                  missing entirely: a white pill in brand purple naming Intimate / Mid-size / Open,
                  which is the one label `MEM-014` AC-3 calls "the single size-related label a
                  member sees" — so leaving it off the marketing card dropped the only size signal
                  the product has. */}
              <div className="relative">
                {event.coverImage === null ? (
                  <div aria-hidden className="h-[140px] bg-spark" />
                ) : (
                  <img
                    src={event.coverImage.url}
                    // The API supplies a description or `null`; an empty alt is correct for a
                    // decorative cover whose event name is directly beneath it.
                    alt={event.coverImage.description ?? ''}
                    className="h-[140px] w-full object-cover"
                  />
                )}
                {event.atmosphere != null && (
                  <span className="absolute left-3 top-3 rounded-pill bg-white px-2.5 py-1 font-brand text-[11px] font-bold text-brand">
                    {event.atmosphere.displayName}
                  </span>
                )}
              </div>
              <CardContent className="pt-[18px]">
                {/* The accent, not the primary. The design sets the date line in `--brand-accent`
                    — the magenta — and ours was purple, which made the whole card read cooler
                    than the comp. */}
                <p className="font-brand text-xs font-semibold uppercase text-brand-accent">
                  {startsAtLabel(event)}
                </p>
                <h3 className="mt-1.5 text-lg font-semibold group-hover:underline">
                  {event.name}
                </h3>
                {/* Where it is. The card carried a "View" link instead, which is a control the
                    design does not have — the whole card's name is already the link — and it
                    replaced the one fact a reader actually scans for. */}
                <div className="mt-2 flex flex-wrap gap-3.5 text-sm text-muted-foreground">
                  {/* The design prints the city. It resolves from the event's linked venue
                      record, so an event whose address was typed rather than chosen has none —
                      the venue's own name is the honest fallback rather than an empty row. */}
                  {placeLabel(event) !== '' && (
                    <span className="inline-flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5" aria-hidden />
                      {placeLabel(event)}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
            </Link>
          </li>
        ))}
      </ul>
    </Band>
  );
}

/**
 * The date, **in the event's own timezone** rather than the reader's (`MEM-015` AC-2).
 *
 * An event at 8:30 in Providence is at 8:30 for everybody reading about it, and rendering it in
 * the visitor's zone is how somebody in London is told a breakfast starts at half past one.
 */
function startsAtLabel(event: DiscoverEventCard): string {
  const when = new Date(event.startsAt);
  if (Number.isNaN(when.getTime())) return '';
  const zone = event.timezone === null ? {} : { timeZone: event.timezone };
  const day = when.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    ...zone,
  });
  // The design's line is "THU, JAN 14 · 8:30 AM" — the **time is part of it**, and it was
  // missing. A card that says only the day makes a reader open the event to learn whether it is
  // a breakfast or an evening thing, which is the one question this line exists to answer.
  //
  // Rendered in the event's own timezone, never the reader's, for the reason `MEM-015` AC-2
  // gives: an event happens where it happens.
  const time = when.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', ...zone });
  return `${day} · ${time}`;
}

/** City and region as the design prints them, falling back to the venue when there is no city. */
function placeLabel(event: DiscoverEventCard): string {
  const place = [event.city, event.region].filter((part) => part !== null && part !== '').join(', ');
  return place === '' ? (event.venueName ?? '') : place;
}
